import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Paper, 
    Button, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    TablePagination,
    TableSortLabel,
    Toolbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Chip,
    Grid,
    Card,
    CardContent,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
    Divider,
    FormControlLabel,
    Switch,
    alpha,
    FormGroup
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Build as MaintenanceIcon,
    Assignment as AssignmentIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    PersonAddAlt1 as PersonAddIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from './utils/axios';
import PropTypes from 'prop-types';

// Redux imports
import {
    getdataassets,
    addAsset,
    updateAsset,
    deleteAsset,
    setLoading,
    setError,
    clearError,
    setSearchTerm,
    setFilterType,
    setFilterStatus,
    setFilterLocation,
    setSortBy,
    setSortOrder,
    setPage,
    setRowsPerPage,
    setSelectedAsset,
    setDialogOpen,
    setEditingAsset,
    clearFilters
} from './redux/assets';

// Enhanced Table Head component
function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    const headCells = [
        {
            id: 'assetNumber',
            numeric: false,
            disablePadding: false,
            label: 'Asset Number',
        },
        {
            id: 'name',
            numeric: false,
            disablePadding: false,
            label: 'Asset Name',
        },
        {
            id: 'type',
            numeric: false,
            disablePadding: false,
            label: 'Type',
        },
        {
            id: 'location',
            numeric: false,
            disablePadding: false,
            label: 'Location',
        },
        {
            id: 'status',
            numeric: false,
            disablePadding: false,
            label: 'Status',
        },
        {
            id: 'condition',
            numeric: false,
            disablePadding: false,
            label: 'Condition',
        },
        {
            id: 'purchaseCost',
            numeric: true,
            disablePadding: false,
            label: 'Purchase Cost',
        },
        {
            id: '',
            numeric: true,
            disablePadding: false,
            label: '',
        }
    ];

    return (
        <TableHead>
            <TableRow>
                <TableCell align="center">
                    <b>No.</b>
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            <b>{headCell.label}</b>
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={{ display: 'none' }}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

// Enhanced Table Toolbar component
const EnhancedTableToolbar = (props) => {
    const { numSelected } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            <Typography
                sx={{ flex: '1 1 100%' }}
                variant="h6"
                id="tableTitle"
                component="div"
            >
                ASSETS MANAGEMENT
            </Typography>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

// Utility functions for sorting
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const AssetManagement = () => {
    const dispatch = useDispatch();
    
    // Get Redux state
    const assetsState = useSelector((state) => state.assets) || {
        assets: [],
        loading: {
            fetch: false,
            add: false,
            update: false,
            delete: false
        },
        error: null,
        searchTerm: '',
        filterType: 'all',
        filterStatus: 'all',
        filterLocation: 'all',
        sortBy: 'assetNumber',
        sortOrder: 'asc',
        page: 0,
        rowsPerPage: 10,
        selectedAsset: null,
        dialogOpen: false,
        editingAsset: null
    };

    const {
        assets,
        loading,
        error,
        searchTerm,
        filterType,
        filterStatus,
        filterLocation,
        sortBy,
        sortOrder,
        page,
        rowsPerPage,
        selectedAsset,
        dialogOpen,
        editingAsset
    } = assetsState;

    // Get admin state
    const admin = useSelector((state) => state.admin);

    // Local state for form data
    const [formData, setFormData] = useState({
        name: '',
        assetNumber: '',
        type: '',
        purchaseCost: '',
        location: '',
        description: '',
        supplier: '',
        warrantyExpiry: '',
        maintenanceSchedule: 'none',
        status: 'Active',
        condition: 'Good'
    });

    // Asset types and conditions
    const assetTypes = [
        'Equipment', 'Furniture', 'Vehicle', 'Building', 'Technology', 'Books', 'Other'
    ];
    
    const assetConditions = [
        'Excellent', 'Good', 'Fair', 'Poor', 'Critical'
    ];
    
    const assetStatuses = [
        'Active', 'Inactive', 'Under Maintenance', 'Retired', 'Lost', 'Damaged'
    ];
    
    const maintenanceSchedules = [
        'none', 'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly', 'As Needed'
    ];

    // Fetch assets on component mount
    useEffect(() => {
        fetchAssets();
    }, []);

    const fetchAssets = async () => {
        try {
            dispatch(setLoading({ fetch: true }));
            const response = await axiosInstance.post('https://servermain.sisrevolution.com/api/assets/getassets', {
                schoolID: admin.schoolID
            });
            
            if (response.status === 200) {
                dispatch(getdataassets({ data: response.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching assets:', error);
            dispatch(setError('Error fetching assets. Please try again.'));
        } finally {
            dispatch(setLoading({ fetch: false }));
        }
    };

    const handleAddAsset = async () => {
        try {
            dispatch(setLoading({ add: true }));
            const assetData = {
                schoolID: admin.schoolID,
                assetName: formData.name,
                assetType: formData.type,
                assetNumber: formData.assetNumber,
                assetCategory: 'General',
                purchaseDate: new Date().toISOString().split('T')[0],
                purchaseCost: parseFloat(formData.purchaseCost) || 0,
                supplier: formData.supplier || '',
                location: formData.location || '',
                assignedTo: '',
                description: formData.description || '',
                warrantyExpiry: formData.warrantyExpiry || null,
                maintenanceSchedule: formData.maintenanceSchedule || 'Monthly',
                status: formData.status || 'Active'
            };

            const response = await axiosInstance.post('https://servermain.sisrevolution.com/api/assets/registerasset', assetData);
            
            if (response.status === 200 || response.status === 201) {
                dispatch(addAsset(response.data.asset));
                dispatch(setDialogOpen(false));
                resetForm();
            }
        } catch (error) {
            console.error('Error adding asset:', error);
            dispatch(setError(error.response?.data?.error || 'Error adding asset. Please try again.'));
        } finally {
            dispatch(setLoading({ add: false }));
        }
    };

    const handleEditAsset = async () => {
        try {
            dispatch(setLoading({ update: true }));
            const assetData = {
                assetID: editingAsset._id,
                schoolID: admin.schoolID,
                assetName: formData.name,
                assetType: formData.type,
                assetNumber: formData.assetNumber,
                assetCategory: 'General',
                purchaseDate: new Date().toISOString().split('T')[0],
                purchaseCost: parseFloat(formData.purchaseCost) || 0,
                supplier: formData.supplier || '',
                location: formData.location || '',
                assignedTo: '',
                description: formData.description || '',
                warrantyExpiry: formData.warrantyExpiry || null,
                maintenanceSchedule: formData.maintenanceSchedule || 'Monthly',
                status: formData.status || 'Active'
            };

            const response = await axiosInstance.put('https://servermain.sisrevolution.com/api/assets/updateasset', assetData);
            
            if (response.status === 200) {
                dispatch(updateAsset(response.data.asset));
                dispatch(setDialogOpen(false));
                dispatch(setEditingAsset(null));
                resetForm();
            }
        } catch (error) {
            console.error('Error updating asset:', error);
            dispatch(setError(error.response?.data?.error || 'Error updating asset. Please try again.'));
        } finally {
            dispatch(setLoading({ update: false }));
        }
    };

    const handleDeleteAsset = async (assetId) => {
        if (window.confirm('Are you sure you want to delete this asset?')) {
            try {
                dispatch(setLoading({ delete: true }));
                const response = await axiosInstance.delete('https://servermain.sisrevolution.com/api/assets/deleteasset', {
                    data: { _id: assetId, schoolID: admin.schoolID }
                });
                
                if (response.status === 200) {
                    dispatch(deleteAsset(assetId));
                }
            } catch (error) {
                console.error('Error deleting asset:', error);
                dispatch(setError(error.response?.data?.error || 'Error deleting asset. Please try again.'));
            } finally {
                dispatch(setLoading({ delete: false }));
            }
        }
    };

    // Filter and sort assets
    const filteredAssets = assets.filter(asset => {
        const matchesSearch = asset.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             asset.assetNumber?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || asset.assetType === filterType;
        const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
        const matchesLocation = filterLocation === 'all' || asset.location === filterLocation;
        
        return matchesSearch && matchesType && matchesStatus && matchesLocation;
    });

    const sortedAssets = [...filteredAssets].sort((a, b) => {
        const aValue = a[sortBy] || '';
        const bValue = b[sortBy] || '';
        
        if (sortOrder === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
    });

    // Pagination
    const paginatedAssets = sortedAssets.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Dialog handlers
    const openAddDialog = () => {
        dispatch(setEditingAsset(null));
        dispatch(setDialogOpen(true));
        resetForm();
    };

    const openEditDialog = (asset) => {
        dispatch(setEditingAsset(asset));
        setFormData({
            name: asset.assetName || '',
            assetNumber: asset.assetNumber || '',
            type: asset.assetType || '',
            purchaseCost: asset.purchaseCost || '',
            location: asset.location || '',
            description: asset.description || '',
            supplier: asset.supplier || '',
            warrantyExpiry: asset.warrantyExpiry || '',
            maintenanceSchedule: asset.maintenanceSchedule || 'none',
            status: asset.status || 'Active',
            condition: asset.condition || 'Good'
        });
        dispatch(setDialogOpen(true));
    };

    const closeDialog = () => {
        dispatch(setDialogOpen(false));
        dispatch(setEditingAsset(null));
        resetForm();
    };

    const resetForm = () => {
        setFormData({
            name: '',
            assetNumber: '',
            type: '',
            purchaseCost: '',
            location: '',
            description: '',
            supplier: '',
            warrantyExpiry: '',
            maintenanceSchedule: 'none',
            status: 'Active',
            condition: 'Good'
        });
    };

    // Add the missing handleInputChange function
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Table event handlers
    const handleRequestSort = (event, property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        dispatch(setSortOrder(isAsc ? 'desc' : 'asc'));
        dispatch(setSortBy(property));
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = assets.map((n) => n.name);
            // setSelected(newSelecteds); // This state is managed by Redux
            return;
        }
        // setSelected([]); // This state is managed by Redux
    };

    const handleChangePage = (event, newPage) => {
        dispatch(setPage(newPage));
    };

    const handleChangeRowsPerPage = (event) => {
        dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
        dispatch(setPage(0));
    };

    const handleChangeDense = (event) => {
        // setDense(event.target.checked); // This state is managed by Redux
    };

    // Filter and search assets
    // const filteredAssets = assets.filter(asset => { // This is now handled by Redux
    //     const matchesSearch = asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //                         asset.assetNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //                         asset.location?.toLowerCase().includes(searchTerm.toLowerCase());
        
    //     const matchesType = filterType === 'all' || asset.type === filterType;
    //     const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
        
    //     return matchesSearch && matchesType && matchesStatus;
    // });

    // Calculate statistics
    const totalAssets = assets.length;
    const activeAssets = assets.filter(asset => asset.status === 'Active').length;
    const totalValue = assets.reduce((sum, asset) => sum + (parseFloat(asset.purchaseCost) || 0), 0);
    const maintenanceDue = assets.filter(asset => asset.status === 'Under Maintenance').length;

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Inactive': return 'default';
            case 'Under Maintenance': return 'warning';
            case 'Retired': return 'error';
            case 'Lost': return 'error';
            case 'Damaged': return 'error';
            default: return 'default';
        }
    };

    const getConditionColor = (condition) => {
        switch (condition) {
            case 'Excellent': return 'success';
            case 'Good': return 'success';
            case 'Fair': return 'warning';
            case 'Poor': return 'error';
            case 'Critical': return 'error';
            default: return 'default';
        }
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredAssets.length) : 0;
    
    return (
        <Box sx={{ width: '100%' }}>
            {/* Error Alert */}
            {error && (
                <Alert 
                    severity="error" 
                    onClose={() => dispatch(clearError())}
                    sx={{ mb: 2 }}
                >
                    {error}
                </Alert>
            )}

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Assets
                            </Typography>
                            <Typography variant="h4" component="h2">
                                {totalAssets}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Active Assets
                            </Typography>
                            <Typography variant="h4" component="h2" color="success.main">
                                {activeAssets}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Total Value
                            </Typography>
                            <Typography variant="h4" component="h2" color="primary.main">
                                GHC {totalValue.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Under Maintenance
                </Typography>
                            <Typography variant="h4" component="h2" color="warning.main">
                                {maintenanceDue}
                </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Table */}
            <Paper sx={{ width: '100%', mb: 2 }}>
                <EnhancedTableToolbar numSelected={selectedAsset ? 1 : 0} />
                
                <Grid container>
                    <Grid lg={1} xs={1} md={1} style={{ textAlign: "center" , marginLeft: 5}}>
                        <FormControl style={{ marginRight: 2, minWidth: 80, padding: 0, marginTop: 10, borderColor: "#2196f3" }}>
                            <Select
                                id="demo-simple-select-autowidth"
                                value={filterType}
                                onChange={(e) => dispatch(setFilterType(e.target.value))}
                                autoWidth
                                style={{ height: "38px", width: "100%", padding: 0, color: "grey", borderColor: "#2196f3", outline: "none" }}
                            >
                                <Container>
                                    <p align="center" style={{margin:5}} ><b>Asset Type</b></p>
                                    <Divider/>
                                    <FormGroup style={{marginTop:10, marginBottom:10}}>
                                        <FormControlLabel control={<Switch checked={filterType === 'Equipment'} onChange={(e) => dispatch(setFilterType(e.target.checked ? 'Equipment' : 'all'))} />} label="Equipment" />
                                        <FormControlLabel control={<Switch checked={filterType === 'Furniture'} onChange={(e) => dispatch(setFilterType(e.target.checked ? 'Furniture' : 'all'))} />} label="Furniture" />
                                        <FormControlLabel control={<Switch checked={filterType === 'Technology'} onChange={(e) => dispatch(setFilterType(e.target.checked ? 'Technology' : 'all'))} />} label="Technology" />
                                    </FormGroup>
                                    
                                    <p align="center" style={{margin:5}} ><b>Sort by Status</b></p>
                                    <Divider/>
                                </Container>

                                <MenuItem value="all">All Types</MenuItem>
                                {assetTypes && assetTypes.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid lg={2} xs={2} md={2} style={{ textAlign: "center" }}>
                        <FormControl style={{ marginRight: 2, minWidth: 80, padding: 0, marginTop: 10, borderColor: "#2196f3" }}>
                            <Select
                                id="demo-simple-select-autowidth-status"
                                value={filterStatus}
                                onChange={(e) => dispatch(setFilterStatus(e.target.value))}
                                autoWidth
                                style={{ height: "38px", width: "100%", padding: 0, color: "grey", borderColor: "#2196f3", outline: "none" }}
                            >
                                <Container>
                                    <p align="center" style={{margin:5}} ><b>Asset Status</b></p>
                                    <Divider/>
                                    <FormGroup style={{marginTop:10, marginBottom:10}}>
                                        <FormControlLabel control={<Switch checked={filterStatus === 'Active'} onChange={(e) => dispatch(setFilterStatus(e.target.checked ? 'Active' : 'all'))} />} label="Active" />
                                        <FormControlLabel control={<Switch checked={filterStatus === 'Under Maintenance'} onChange={(e) => dispatch(setFilterStatus(e.target.checked ? 'Under Maintenance' : 'all'))} />} label="Maintenance" />
                                        <FormControlLabel control={<Switch checked={filterStatus === 'Inactive'} onChange={(e) => dispatch(setFilterStatus(e.target.checked ? 'Inactive' : 'all'))} />} label="Inactive" />
                                    </FormGroup>
                                    
                                    <p align="center" style={{margin:5}} ><b>Filter Options</b></p>
                                    <Divider/>
                                </Container>

                                <MenuItem value="all">All Statuses</MenuItem>
                                {assetStatuses.map(status => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

   {/* Add Asset Button - positioned like in salaries component */}
   <Box sx={{ mb: 2, textAlign: 'left', marginRight: 5}}>
                <Tooltip title="Add Asset">
                    <Button
                        aria-label="add asset"
                        edge="start"
                        variant="outlined"
                        color="primary"
                        style={{marginTop:10.5}}
                        onClick={openAddDialog}
                    >
                        Add Asset
                    </Button>
                </Tooltip>
            </Box>
            
                    <Grid lg={3} xs={10} md={6}>
                        <SearchIcon id="searchbox" style={{ position: "absolute", marginTop: "18px", marginLeft: "8px", color: "grey" }} /> 
                        <input 
                            type="search" 
                            onChange={(e) => dispatch(setSearchTerm(e.target.value))} 
                            placeholder="Search" 
                            style={{ 
                                padding: 10, 
                                paddingLeft: "38px", 
                                marginTop: 10, 
                                width: "100%", 
                                border: "solid", 
                                borderRadius: "5px", 
                                borderWidth: "0.5px", 
                                backgroundColor: "rgba(65,105,225, 0.05)", 
                                borderColor: "#2196f3" 
                            }} 
                        />
                    </Grid>

                    <Grid lg={4} sm={12} md={12}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={filteredAssets.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                </Grid>
                <br />
                <Divider />
                
                <TableContainer>
                    <Table
                        sx={{ minWidth: "100%" }}
                        aria-labelledby="tableTitle"
                        size={loading.fetch ? 'small' : 'medium'} // Use loading state for table size
                    >
                        <EnhancedTableHead
                            numSelected={selectedAsset ? 1 : 0} // Use Redux state
                            order={sortOrder} // Use Redux state
                            orderBy={sortBy} // Use Redux state
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={filteredAssets.length}
                        />

                        <TableBody>
                            {stableSort(filteredAssets, getComparator(sortOrder, sortBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((asset, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow hover key={asset._id}>
                                            <TableCell style={{ textAlign: "center" }}>
                                                <span>
                                                    {(index + 1 + (page * rowsPerPage)) + "."}
                                                </span>
                                            </TableCell>
                                            <TableCell align="left">{asset.assetNumber}</TableCell>
                                            <TableCell component="th" id={labelId} scope="row" padding="none" align="left">
                                                <Typography variant="subtitle2">
                                                    {asset.assetName}
                </Typography>
                                                {asset.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {asset.description}
                </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="left">{asset.assetType}</TableCell>
                                            <TableCell align="left">{asset.location}</TableCell>
                                            <TableCell align="left">
                                                {asset.status === "Active" ? 
                                                    <span style={{padding:8, borderRadius:"20px", backgroundColor:"lightgreen"}}>{asset.status}</span> : 
                                                    asset.status === "Under Maintenance" ? 
                                                        <span style={{padding:8, borderRadius:"20px", backgroundColor:"orange"}}>{asset.status}</span> :
                                                        <span style={{padding:8, borderRadius:"20px", backgroundColor:"pink"}}>{asset.status}</span>
                                                }
                                            </TableCell>
                                            <TableCell align="left">
                                                {asset.condition === "Excellent" || asset.condition === "Good" ? 
                                                    <span style={{padding:8, borderRadius:"20px", backgroundColor:"lightgreen"}}>{asset.condition}</span> : 
                                                    asset.condition === "Fair" ? 
                                                        <span style={{padding:8, borderRadius:"20px", backgroundColor:"orange"}}>{asset.condition}</span> :
                                                        <span style={{padding:8, borderRadius:"20px", backgroundColor:"pink"}}>{asset.condition}</span>
                                                }
                                            </TableCell>
                                            <TableCell align="left">
                                                GHC {parseFloat(asset.purchaseCost || 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell align="left">
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" color="primary">
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Asset">
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={() => openEditDialog(asset)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Asset">
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={() => handleDeleteAsset(asset._id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (loading.fetch ? 33 : 53) * emptyRows, // Use loading state for height
                                    }}
                                >
                                    <TableCell colSpan={9} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            
         

            <FormControlLabel
                control={<Switch checked={loading.fetch} onChange={handleChangeDense} />} // Use loading state for switch
                label="Dense padding"
            />

            {/* Add/Edit Asset Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {editingAsset ? 'Edit Asset' : 'Add New Asset'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Asset Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Asset Number"
                                value={formData.assetNumber}
                                onChange={(e) => handleInputChange('assetNumber', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Asset Type</InputLabel>
                                <Select
                                    value={formData.type}
                                    onChange={(e) => handleInputChange('type', e.target.value)}
                                    label="Asset Type"
                                    required
                                >
                                    {assetTypes && assetTypes.map(type => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Purchase Cost (GHC)"
                                type="number"
                                value={formData.purchaseCost}
                                onChange={(e) => handleInputChange('purchaseCost', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={formData.location}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    label="Status"
                                >
                                    {assetStatuses.map(status => (
                                        <MenuItem key={status} value={status}>{status}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Condition</InputLabel>
                                <Select
                                    value={formData.condition}
                                    onChange={(e) => handleInputChange('condition', e.target.value)}
                                    label="Condition"
                                >
                                    {assetConditions.map(condition => (
                                        <MenuItem key={condition} value={condition}>{condition}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Maintenance Schedule</InputLabel>
                                <Select
                                    value={formData.maintenanceSchedule}
                                    onChange={(e) => handleInputChange('maintenanceSchedule', e.target.value)}
                                    label="Maintenance Schedule"
                                >
                                    {maintenanceSchedules.map(schedule => (
                                        <MenuItem key={schedule} value={schedule}>
                                            {schedule === 'none' ? 'No Schedule' : schedule}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Supplier"
                                value={formData.supplier}
                                onChange={(e) => handleInputChange('supplier', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Warranty Expiry"
                                type="date"
                                value={formData.warrantyExpiry}
                                onChange={(e) => handleInputChange('warrantyExpiry', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button 
                        onClick={editingAsset ? handleEditAsset : handleAddAsset}
                        variant="contained"
                        disabled={loading.add || loading.update} // Use loading state for buttons
                    >
                        {loading.add || loading.update ? <CircularProgress size={20} /> : (editingAsset ? 'Update Asset' : 'Add Asset')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar
                open={error !== null} // Use error state for snackbar
                autoHideDuration={6000}
                onClose={() => dispatch(clearError())}
            >
                <Alert 
                    onClose={() => dispatch(clearError())} 
                    severity="error" // Always error for now
                >
                    {error || 'An error occurred.'}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AssetManagement; 